import { VideoData } from '@lumen5/luminary';

import { runDesignJob } from '../../html-design/designJob';
import { buildHtmlDesignTools } from '../../html-design/htmlDesignTools';
import { getHtmlDesignSystemPrompt } from '../../html-design/systemPrompt';
import { Model, ReasoningEffort } from '../../types';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../../types';
import { ConversationTool } from '../ConversationTool';
import { AddSceneTool } from '../AddSceneTool';
import { resolveSceneById } from '../scene-addressing';
import {
    DESIGN_MOTION_GRAPHICS_SCENE_TOOL_NAME,
    MOTION_GRAPHICS_SCENE_DESIGN_ID,
} from './motionGraphicsSceneDesign';

import { checkpointVideo } from 'js/src/data/video-data';
import { saveRawLuminary } from 'js/src/luminary/utils';

export interface DesignMotionGraphicsSceneArgs {
    scene_id: string;
    brief: string;
}

const DESIGN_SUBAGENT_MAX_ITERATIONS = 20;

/**
 * Build or change the graphic on a motion graphics scene by delegating to a design-subagent
 * conversation. One tool covers both cases because the subagent is handed the current composition
 * either way, which is also why a fresh conversation per job is fine. Addressed by scene_id rather
 * than position so that designing several scenes in one turn doesn't depend on the order they
 * finish.
 */
export class DesignMotionGraphicsSceneTool extends ConversationTool<DesignMotionGraphicsSceneArgs> {
    public static readonly toolName = DESIGN_MOTION_GRAPHICS_SCENE_TOOL_NAME;
    public override canRunAsync = true;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    public override readonly childReasoningEffort = ReasoningEffort.LOW;
    public override expandedByDefault = true;

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: DesignMotionGraphicsSceneTool.toolName,
                description: 'Build or change the graphic on a motion graphics scene.',
                parameters: {
                    type: 'object',
                    properties: {
                        scene_id: {
                            type: 'string',
                            description: 'The motion graphics scene to design.',
                        },
                        brief: {
                            type: 'string',
                            description:
                                'What the graphic must communicate and show, or what to change.',
                        },
                    },
                    required: ['scene_id', 'brief'],
                },
            },
        };
    }

    public getToolDisplayName(): string {
        return 'Designing motion graphics scene';
    }

    public get statusIcon(): string {
        return 'brush';
    }

    public async execute(
        args: DesignMotionGraphicsSceneArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = resolveSceneById(this.luminary, this.getState(), args.scene_id);
        if ('error' in resolved) {
            return { result: `Error: ${resolved.error}`, isSuccess: false };
        }
        const { scene } = resolved;
        const asset = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0];
        if (!asset) {
            return {
                result:
                    `Error: scene ${args.scene_id} is not a motion graphics scene — it has no ` +
                    `dynamic graphic to design. Add one with ${AddSceneTool.toolName} using ` +
                    'scene_design_id ' +
                    `"${MOTION_GRAPHICS_SCENE_DESIGN_ID}", or use the ` +
                    'regular scene tools to change this scene.',
                isSuccess: false,
            };
        }

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: getHtmlDesignSystemPrompt(),
            tools: buildHtmlDesignTools(this.luminary, this.dispatch, this.getState),
            maxIterations: DESIGN_SUBAGENT_MAX_ITERATIONS,
            task: 'html-design',
            requiresVideoState: false,
            streaming: true,
        });

        const job = await runDesignJob({
            conversation,
            luminary: this.luminary,
            dispatch: this.dispatch,
            getState: this.getState,
            sceneId: scene.id,
            assetId: asset.id,
            brief: args.brief,
            parentActionGroup: context.parentActionGroup,
        });

        if (!job.success) {
            return {
                result: `Error: designing motion graphics scene ${args.scene_id} failed: ${job.summary}`,
                isSuccess: false,
            };
        }

        this.dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(this.luminary),
                systemTriggered: false,
                parentActionGroup: context.parentActionGroup,
            }),
        );

        return {
            result: `Success: designed the motion graphics scene ${args.scene_id}. ${job.summary}`,
            isSuccess: true,
        };
    }
}
